import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Container, Input, Spinner, Table } from 'reactstrap';
import { toast } from 'react-toastify';

import { fetchTasksMe, RM_TASKS_ENABLED, tasksApi } from '../../../Tasks/tasksApi';
import { ACCENT } from './taskTheme';

/**
 * Consumer Xperience → Task Insights.
 *
 * Renewal Insights asks one question — did they pay? — and a funnel answers
 * it. Every other type answers "what came of it?", which needs two
 * breakdowns instead: why the closed ones closed, and what is happening on
 * the ones still open.
 *
 * Same filters as the renewal screen so the two read alike, plus the type.
 * With no type chosen it describes the whole queue.
 */

interface Slice {
  key: string;
  label: string;
  count: number;
  pct: number;
  tone?: 'success' | 'fail' | 'neutral';
}
interface Insights {
  window: { from: string; to: string; days: number; tz: string };
  types: { key: string; name: string; count: number }[];
  totals: {
    created: number; open: number; inProgress: number; closed: number;
    untouched: number; good: number; bad: number; neutral: number;
  };
  closedBy: Slice[];
  openBy: Slice[];
  answers: { field: string; label: string; options: Slice[] }[];
  byAgent: {
    externalId: string; name: string; created: number;
    closed: number; good: number; open: number; untouched: number;
  }[];
}

const iso = (d: Date): string => d.toISOString().slice(0, 10);
const today = (): string => iso(new Date());
const daysAgo = (n: number): string => iso(new Date(Date.now() - n * 864e5));
const monthStart = (): string => `${today().slice(0, 7)}-01`;
const num = (n: number): string => n.toLocaleString();

/** The same periods Team Performance offers, so the two read alike. */
type Preset = 'mtd' | '7d' | '30d' | 'custom';

type SortKey = 'name' | 'created' | 'closed' | 'good' | 'open' | 'untouched';

const BY_RM_COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'name', label: 'RM', numeric: false },
  { key: 'created', label: 'Raised', numeric: true },
  { key: 'closed', label: 'Closed', numeric: true },
  { key: 'good', label: 'Good outcome', numeric: true },
  { key: 'open', label: 'Still open', numeric: true },
  { key: 'untouched', label: 'Never touched', numeric: true },
];

const TONE: Record<string, string> = {
  success: '#15803d',
  fail: '#b91c1c',
  neutral: '#b45309',
};

const Stat: React.FC<{ label: string; value: string; sub?: string; tone?: string; title?: string }> = ({
  label, value, sub, tone, title,
}) => (
  <div
    className="border rounded px-3 py-2"
    title={title}
    style={{ minWidth: 132, background: '#fff', cursor: title ? 'help' : undefined }}
  >
    <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div className="fw-bold" style={{ fontSize: 22, color: tone ?? 'inherit', fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </div>
    {sub && <div className="text-muted" style={{ fontSize: 12 }}>{sub}</div>}
  </div>
);

/** A breakdown as a bar list — the shape reads faster than a pie at this size. */
const Breakdown: React.FC<{ title: string; hint: string; rows: Slice[]; empty: string }> = ({
  title, hint, rows, empty,
}) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card className="h-100">
      <CardBody>
        <h5 className="mb-1" style={{ fontSize: 15 }}>{title}</h5>
        <div className="text-muted mb-3" style={{ fontSize: 12 }}>{hint}</div>
        {rows.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 13 }}>{empty}</div>
        ) : (
          rows.map((r) => (
            <div key={r.key} className="mb-2">
              <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                <span style={{ color: r.tone ? TONE[r.tone] : 'inherit' }}>{r.label}</span>
                <span className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {num(r.count)} · {r.pct}%
                </span>
              </div>
              <div style={{ height: 6, background: '#f1f1f6', borderRadius: 3 }}>
                <div
                  style={{
                    width: `${(r.count / max) * 100}%`,
                    height: '100%',
                    borderRadius: 3,
                    background: r.tone ? TONE[r.tone] : ACCENT,
                  }}
                />
              </div>
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );
};

const TaskInsightsPage: React.FC = () => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [data, setData] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);
  // This month rather than a wide default: the window decides how much is
  // scanned, and an RM opening the page wants the month they are in.
  const [preset, setPreset] = useState<Preset>('mtd');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [agent, setAgent] = useState('');
  const [pod, setPod] = useState('');
  const [country, setCountry] = useState('');
  const [type, setType] = useState('');
  const [pods, setPods] = useState<{ key: string; name: string }[]>([]);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);
  /** Kept apart from the results, which are already scoped — see Renewal Insights. */
  const [roster, setRoster] = useState<{ externalId: string; name: string; pods: string[] }[]>([]);
  /** Every type, so the picker does not shrink to whatever the filters left. */
  const [allTypes, setAllTypes] = useState<{ key: string; name: string }[]>([]);
  /** By-RM table sorting, in the browser — the rows are already all here. */
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'mtd') { setFrom(monthStart()); setTo(today()); }
    else if (p === '7d') { setFrom(daysAgo(6)); setTo(today()); }
    else if (p === '30d') { setFrom(daysAgo(29)); setTo(today()); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ from, to, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      if (agent) p.set('agentExternalId', agent);
      if (pod) p.set('podKey', pod);
      if (country) p.set('country', country);
      if (type) p.set('taskTypeKey', type);
      setData(await tasksApi<Insights>(`/v1/task-insights?${p.toString()}`));
    } catch (err: any) {
      toast.error(err.message || 'Could not load task insights');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, agent, pod, country, type]);

  useEffect(() => { fetchTasksMe().then((m) => setAllowed(!!m)); }, []);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    tasksApi<{ key: string; name: string }[]>('/v1/task-types')
      .then((t) => setAllTypes((t ?? []).map((x) => ({ key: x.key, name: x.name }))))
      .catch(() => undefined);
    tasksApi<{
      pods: {
        key: string; name: string;
        countries: { countryCode: string; name: string; learners: number }[];
        agents: { externalId: string; name: string; role: string; isActive: boolean }[];
      }[];
    }>('/v1/pods')
      .then((d) => {
        setPods((d.pods ?? []).map((x) => ({ key: x.key, name: x.name })));
        const byAgent = new Map<string, { externalId: string; name: string; pods: string[] }>();
        for (const p of d.pods ?? []) {
          for (const a of p.agents ?? []) {
            if (a.role !== 'AGENT' || !a.isActive) continue;
            const prev = byAgent.get(a.externalId);
            if (prev) prev.pods.push(p.key);
            else byAgent.set(a.externalId, { externalId: a.externalId, name: a.name, pods: [p.key] });
          }
        }
        setRoster(Array.from(byAgent.values()).sort((x, y) => x.name.localeCompare(y.name)));
        const seen = new Map<string, { code: string; name: string; learners: number }>();
        for (const p of d.pods ?? []) {
          for (const c of p.countries ?? []) {
            const prev = seen.get(c.countryCode);
            if (!prev || c.learners > prev.learners) {
              seen.set(c.countryCode, { code: c.countryCode, name: c.name, learners: c.learners });
            }
          }
        }
        setCountries(
          Array.from(seen.values())
            .filter((c) => c.learners > 0)
            .sort((a, b) => b.learners - a.learners || a.name.localeCompare(b.name))
            .map(({ code, name }) => ({ code, name })),
        );
      })
      .catch(() => undefined);
  }, []);

  const byAgentSorted = useMemo(() => {
    const rows = [...(data?.byAgent ?? [])];
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) =>
      sortKey === 'name'
        ? a.name.localeCompare(b.name) * dir
        : ((a[sortKey] as number) - (b[sortKey] as number)) * dir ||
          a.name.localeCompare(b.name),
    );
    return rows;
  }, [data, sortKey, sortDir]);

  /** Numbers open on the largest, names on A–Z — what you want on first click. */
  const sortBy = (col: { key: SortKey; numeric: boolean }) => {
    if (sortKey === col.key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(col.key);
      setSortDir(col.numeric ? 'desc' : 'asc');
    }
  };

  /** The RM list follows the desk filter, never the results. */
  const rmOptions = useMemo(
    () => (pod ? roster.filter((r) => r.pods.includes(pod)) : roster),
    [roster, pod],
  );

  if (!RM_TASKS_ENABLED) {
    return <div className="page-body"><p className="text-muted p-4">This feature isn&apos;t enabled in this environment yet.</p></div>;
  }
  if (allowed === null) return <div className="page-body text-center py-5"><Spinner /></div>;
  if (!allowed) {
    return (
      <div className="page-body">
        <p className="text-muted p-4">
          Task insights are for the RM team — you don&apos;t have an RM record in this tool.
        </p>
      </div>
    );
  }

  const t = data?.totals;

  return (
    <div className="page-body">
      <Container fluid className="pt-4">
        <div className="mb-3">
          <h3 className="mb-0 fw-bold">Task Insights</h3>
          <span className="text-muted">
            What came of the tickets — why the closed ones closed, and what is happening on the
            ones still open.
          </span>
        </div>

        <Card className="mb-3">
          <CardBody className="d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
            <div>
              <label className="text-muted d-block" style={{ fontSize: 11 }}>Period</label>
              <Input type="select" bsSize="sm" style={{ width: 140 }} value={preset}
                onChange={(e) => applyPreset(e.target.value as Preset)}>
                <option value="mtd">This month</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="custom">Custom</option>
              </Input>
            </div>
            {preset === 'custom' && (
              <>
                <div>
                  <label className="text-muted d-block" style={{ fontSize: 11 }}>From</label>
                  <Input type="date" bsSize="sm" style={{ width: 150 }} value={from}
                    onChange={(e) => setFrom(e.target.value)} />
                </div>
                <div>
                  <label className="text-muted d-block" style={{ fontSize: 11 }}>To</label>
                  <Input type="date" bsSize="sm" style={{ width: 150 }} value={to} min={from}
                    onChange={(e) => setTo(e.target.value)} />
                </div>
              </>
            )}
            {/* The one filter this screen adds over Renewal Insights. */}
            <div>
              <label className="text-muted d-block" style={{ fontSize: 11 }}>Task type</label>
              <Input type="select" bsSize="sm" style={{ width: 210 }} value={type}
                onChange={(e) => setType(e.target.value)}>
                <option value="">All task types</option>
                {allTypes.map((x) => (
                  <option key={x.key} value={x.key}>{x.name}</option>
                ))}
              </Input>
            </div>
            <div>
              <label className="text-muted d-block" style={{ fontSize: 11 }}>Desk</label>
              <Input type="select" bsSize="sm" style={{ width: 150 }} value={pod}
                onChange={(e) => { setPod(e.target.value); setAgent(''); }}>
                <option value="">All desks</option>
                {pods.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
              </Input>
            </div>
            <div>
              <label className="text-muted d-block" style={{ fontSize: 11 }}>Country</label>
              <Input type="select" bsSize="sm" style={{ width: 160 }} value={country}
                onChange={(e) => setCountry(e.target.value)}>
                <option value="">All countries</option>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </Input>
            </div>
            <div>
              <label className="text-muted d-block" style={{ fontSize: 11 }}>RM</label>
              <Input type="select" bsSize="sm" style={{ width: 170 }} value={agent}
                onChange={(e) => setAgent(e.target.value)}>
                <option value="">Whole team</option>
                {rmOptions.map((r) => (
                  <option key={r.externalId} value={r.externalId}>{r.name}</option>
                ))}
              </Input>
            </div>
            {loading && <Spinner size="sm" className="mb-1" />}
          </CardBody>
        </Card>

        {!loading && !data && (
          <Card><CardBody className="text-muted">Nothing to show for this selection.</CardBody></Card>
        )}

        {data && t && (
          <>
            <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
              <Stat
                label="Raised"
                value={num(t.created)}
                sub={preset === 'mtd' ? 'this month' : `${data.window.days} days`}
                title={`${data.window.from} to ${data.window.to}`}
              />
              <Stat label="Closed" value={num(t.closed)} tone={TONE.success}
                sub={t.created ? `${Math.round((t.closed / t.created) * 100)}% of raised` : undefined} />
              <Stat label="In progress" value={num(t.inProgress)} sub="being worked" />
              <Stat label="Open" value={num(t.open)} sub="not started" />
              <Stat label="Never touched" value={num(t.untouched)}
                tone={t.untouched > 0 ? '#b45309' : undefined}
                sub={t.created ? `${Math.round((t.untouched / t.created) * 100)}% of raised` : undefined}
                title="Open tickets with no activity, comment or call logged by anyone." />
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-xl-6">
                <Breakdown
                  title="Why the closed ones closed"
                  hint={`${num(t.closed)} closed · ${num(t.good)} good, ${num(t.bad)} not, ${num(t.neutral)} neither`}
                  rows={data.closedBy}
                  empty="Nothing has closed in this window."
                />
              </div>
              <div className="col-12 col-xl-6">
                <Breakdown
                  title="What is happening on the open ones"
                  hint="The last thing an RM logged on each ticket still open"
                  rows={data.openBy}
                  empty="Nothing is open in this window."
                />
              </div>
            </div>

            {data.answers.length > 0 && (
              <div className="row g-3 mb-3">
                {data.answers.map((a) => (
                  <div key={a.field} className="col-12 col-md-6 col-xl-4">
                    <Breakdown
                      title={a.label}
                      hint="What RMs recorded on the call"
                      rows={a.options}
                      empty="Not answered yet."
                    />
                  </div>
                ))}
              </div>
            )}

            <Card>
              <CardBody className="p-0">
                <div className="px-3 pt-3 pb-2">
                  <h5 className="mb-0" style={{ fontSize: 15 }}>By RM</h5>
                </div>
                <Table responsive hover className="mb-0 align-middle" style={{ fontSize: 13.5 }}>
                  <thead>
                    <tr className="text-muted" style={{ fontSize: 12 }}>
                      {BY_RM_COLUMNS.map((c) => (
                        <th
                          key={c.key}
                          className={c.numeric ? 'text-end' : undefined}
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => sortBy(c)}
                          title={`Sort by ${c.label.toLowerCase()}`}
                        >
                          {c.label}
                          <span style={{ opacity: sortKey === c.key ? 1 : 0.25 }}>
                            {' '}{sortKey === c.key && sortDir === 'asc' ? '▲' : '▼'}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byAgentSorted.map((a) => (
                      <tr key={a.externalId || a.name}>
                        <td className="fw-semibold">{a.name}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{num(a.created)}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{num(a.closed)}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums', color: a.good ? TONE.success : undefined }}>{num(a.good)}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{num(a.open)}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums', color: a.untouched ? '#b45309' : undefined }}>{num(a.untouched)}</td>
                      </tr>
                    ))}
                    {byAgentSorted.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-muted py-4">No tickets in this window.</td></tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            <div className="text-muted mt-2" style={{ fontSize: 12 }}>
              Counted by when the ticket was raised, so a ticket raised before this window and
              closed inside it is not here. &ldquo;Good outcome&rdquo; is a closing reason the
              type itself marks as a success.
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default TaskInsightsPage;
